/**
 * Community thread page — /community/t/:id
 *
 * Shows one thread, its replies, the current user's reaction state,
 * and (when permitted) a reply composer. Handles soft-delete (410
 * deleted), admin hide (410 hidden to non-author non-admin), locked
 * threads, and the per-category posting gate via `can_post_reply`
 * surfaced by the API.
 */
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  createReply,
  fetchThread,
  subscribeToThread,
  toggleReaction,
  unsubscribeFromThread,
  type ReactionKind,
  type ReplyRead,
  type TargetType,
  type ThreadDetail,
} from "@/lib/community-api";
import { MarkdownBody } from "@/components/MarkdownBody";
import type { MarkdownComposerProps } from "@/components/MarkdownComposer";
import { RichTextEditor } from "@/components/RichTextEditor";

type ComposerComponent = (props: MarkdownComposerProps) => React.JSX.Element;

const REACTION_OPTIONS: ReactionKind[] = [
  "like",
  "helpful",
  "insightful",
  "thanks",
];

const REACTION_LABEL: Record<ReactionKind, string> = {
  like: "👍 Like",
  helpful: "💡 Helpful",
  insightful: "🤔 Insightful",
  thanks: "🙏 Thanks",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: ThreadDetail }
  | { kind: "not_member" }
  | { kind: "deleted" }
  | { kind: "hidden" }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface CommunityThreadPageProps {
  fetchThreadFn?: typeof fetchThread;
  createReplyFn?: typeof createReply;
  toggleReactionFn?: typeof toggleReaction;
  subscribeFn?: typeof subscribeToThread;
  unsubscribeFn?: typeof unsubscribeFromThread;
  onUnauthenticated?: (returnTo: string) => void;
  /** Test seam — swap the composer (default RichTextEditor; tests pass MarkdownComposer). */
  Composer?: ComposerComponent;
}

export function CommunityThreadPage({
  fetchThreadFn = fetchThread,
  createReplyFn = createReply,
  toggleReactionFn = toggleReaction,
  subscribeFn = subscribeToThread,
  unsubscribeFn = unsubscribeFromThread,
  onUnauthenticated = redirectToLogin,
  Composer = RichTextEditor,
}: CommunityThreadPageProps = {}): React.JSX.Element {
  const params = useParams<{ id: string }>();
  const threadId = params.id ?? "";

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [replyBody, setReplyBody] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await fetchThreadFn(threadId);
      setState({ kind: "ready", data });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          onUnauthenticated(`/community/t/${threadId}`);
          return;
        }
        if (err.status === 403) {
          setState({ kind: "not_member" });
          return;
        }
        if (err.status === 404) {
          setState({ kind: "not_found" });
          return;
        }
        if (err.status === 410) {
          const detail = err.body as
            | { detail?: { blocking?: string } }
            | undefined;
          if (detail?.detail?.blocking === "deleted") {
            setState({ kind: "deleted" });
            return;
          }
          setState({ kind: "hidden" });
          return;
        }
      }
      const msg =
        err instanceof Error ? err.message : "Could not load the thread.";
      setState({ kind: "error", message: msg });
    }
  }, [fetchThreadFn, threadId, onUnauthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleReplySubmit = async (): Promise<void> => {
    setSubmitting(true);
    setReplyError(null);
    try {
      await createReplyFn(threadId, { body_md: replyBody });
      setReplyBody("");
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.body) {
        const body = err.body as { detail?: { blocking?: string; message?: string } };
        setReplyError(
          body.detail?.message ??
            body.detail?.blocking ??
            err.message,
        );
      } else if (err instanceof Error) {
        setReplyError(err.message);
      } else {
        setReplyError("Could not post the reply.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactionToggle = async (
    targetType: TargetType,
    targetId: string,
    reaction: ReactionKind,
  ): Promise<void> => {
    try {
      await toggleReactionFn(targetType, targetId, reaction);
      await load();
    } catch {
      // Silently swallow; the next refresh will reconcile.
    }
  };

  const handleSubscribeToggle = async (subscribed: boolean): Promise<void> => {
    try {
      if (subscribed) {
        await unsubscribeFn(threadId);
      } else {
        await subscribeFn(threadId);
      }
      await load();
    } catch {
      // Silently swallow.
    }
  };

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading the thread…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <SimpleErrorPage
        title="Community"
        message="The community is open to active learners and certified practitioners."
      />
    );
  }

  if (state.kind === "not_found") {
    return (
      <SimpleErrorPage
        title="Thread not found"
        message="That thread doesn't exist."
      />
    );
  }

  if (state.kind === "deleted") {
    return (
      <SimpleErrorPage
        title="Thread removed"
        message="The thread author or an admin deleted this thread."
      />
    );
  }

  if (state.kind === "hidden") {
    return (
      <SimpleErrorPage
        title="Thread hidden"
        message="An admin has hidden this thread pending review."
      />
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { thread, replies, reactions, subscribed, can_post_reply } = state.data;

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community" className="hover:text-foreground">
          Community
        </Link>{" "}
        <span aria-hidden="true">›</span> {thread.title}
      </nav>

      <header className="mt-3 border-b border-border pb-6">
        <div className="flex items-baseline gap-2">
          {thread.is_pinned && <span aria-label="Pinned" title="Pinned">📌</span>}
          {thread.is_locked && (
            <span aria-label="Locked" title="Locked">🔒</span>
          )}
          <h1 className="text-2xl font-bold text-foreground">
            {thread.title}
          </h1>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          by {thread.author.display_name}
          {thread.author.is_certified && " · ✓ Certified"} ·{" "}
          {new Date(thread.created_at).toLocaleDateString()}
        </p>
        <div className="mt-4">
          <MarkdownBody body={thread.body_md} />
        </div>

        <ReactionBar
          reactions={reactions}
          onToggle={(reaction) => {
            void handleReactionToggle("thread", thread.id, reaction);
          }}
        />

        <button
          type="button"
          onClick={() => {
            void handleSubscribeToggle(subscribed);
          }}
          className={`mt-4 inline-flex items-center rounded-md border px-3 py-1 text-xs font-medium ${
            subscribed
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border text-muted-foreground hover:text-foreground"
          }`}
          aria-pressed={subscribed}
        >
          {subscribed ? "Subscribed" : "Subscribe"}
        </button>
      </header>

      <ol className="mt-8 space-y-6">
        {replies.map((reply) => (
          <li key={reply.id}>
            <ReplyRow
              reply={reply}
              onReactionToggle={(reaction) => {
                void handleReactionToggle("reply", reply.id, reaction);
              }}
            />
          </li>
        ))}
        {replies.length === 0 && (
          <p className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            No replies yet. Be the first.
          </p>
        )}
      </ol>

      {/* Reply composer */}
      {thread.is_locked ? (
        <p className="mt-10 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          This thread is locked. No new replies.
        </p>
      ) : can_post_reply ? (
        <form
          className="mt-10 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleReplySubmit();
          }}
        >
          <Composer
            label="Reply"
            value={replyBody}
            onChange={setReplyBody}
            minLength={2}
            maxLength={10_000}
            placeholder="Add to the conversation."
            disabled={submitting}
          />
          {replyError && (
            <p className="text-sm text-red-700" role="alert">
              {replyError}
            </p>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting || replyBody.length < 2}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? "Posting…" : "Post reply"}
            </button>
          </div>
        </form>
      ) : (
        <p className="mt-10 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          Certified clinicians lead this discussion — get certified to post.
          You can still read along.
        </p>
      )}
    </section>
  );
}

function ReactionBar({
  reactions,
  onToggle,
}: {
  reactions: ThreadDetail["reactions"];
  onToggle: (reaction: ReactionKind) => void;
}): React.JSX.Element {
  const countMap = new Map(reactions.map((r) => [r.reaction, r]));
  return (
    <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Reactions">
      {REACTION_OPTIONS.map((reaction) => {
        const summary = countMap.get(reaction);
        const count = summary?.count ?? 0;
        const byMe = summary?.by_me ?? false;
        return (
          <button
            key={reaction}
            type="button"
            aria-pressed={byMe}
            onClick={() => {
              onToggle(reaction);
            }}
            className={`rounded-full border px-3 py-1 text-xs ${
              byMe
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {REACTION_LABEL[reaction]}
            {count > 0 && <span className="ml-1">· {count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function ReplyRow({
  reply,
  onReactionToggle,
}: {
  reply: ReplyRead;
  onReactionToggle: (reaction: ReactionKind) => void;
}): React.JSX.Element {
  return (
    <article className="rounded-lg border border-border bg-white p-4">
      <p className="text-xs text-muted-foreground">
        {reply.author.display_name}
        {reply.author.is_certified && " · ✓ Certified"} ·{" "}
        {new Date(reply.created_at).toLocaleDateString()}
      </p>
      <div className="mt-2">
        <MarkdownBody body={reply.body_md} />
      </div>
      <ReactionBar reactions={reply.reactions} onToggle={onReactionToggle} />
    </article>
  );
}

function SimpleErrorPage({
  title,
  message,
}: {
  title: string;
  message: string;
}): React.JSX.Element {
  return (
    <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="mt-4 text-muted-foreground">{message}</p>
      <Link
        to="/community"
        className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
      >
        Back to community
      </Link>
    </section>
  );
}
